import { MainLoop } from '@/commons/mainLoop/MainLoop';
import { SceneWithDispatcher } from '@/commons/world/SceneWithDispatcher';
import { Camera, PerspectiveCamera, Scene, WebGLRenderer, type OffscreenCanvas } from 'three';

import type { TLoopInfo } from '@/commons/mainLoop/TLoopInfo';
import type { ContainerSize, IWorld } from '@/commons/world/IWorld';
import type { TWorldParams } from '@/commons/world/TWorldParams';
import type { ArcRotateCamera } from '@/commons/camera/ArcRotateCamera';

/**
 * A world is the main class for init a 3d world
 * Inherit from this class to create your own world
 *
 * It contains a renderer, a scene, a camera and a mainLoop
 * The child class must be instanciated when the DOM is ready ( with a vue component at onMounted for example)
 *
 * It also contains a resize method to resize the canvas when the window is resized
 * The canvas is resized to the container size
 *
 * @TWorldParams : Is the constructor's argument to init the world
 */
class World implements IWorld {
    private static _worlds: IWorld[] = [];

    private static resizerInitialised = false;

    protected _renderer: WebGLRenderer;

    protected _scene: Scene;

    protected _camera: ArcRotateCamera | Camera;

    protected _container: HTMLElement | Window;

    protected _canvas: HTMLCanvasElement | OffscreenCanvas | undefined;

    protected _mainLoop: MainLoop;

    protected _params: TWorldParams;

    get canvas(): HTMLCanvasElement | OffscreenCanvas | undefined {
        return this._canvas;
    }

    get renderer(): WebGLRenderer {
        return this._renderer;
    }

    get scene(): Scene {
        return this._scene;
    }

    get camera(): ArcRotateCamera | Camera {
        return this._camera;
    }

    get mainLoop(): MainLoop {
        return this._mainLoop;
    }

    get worldParams(): TWorldParams {
        return this._params;
    }

    set newRendererCamera(camera: Camera) {
        this._camera = camera;
        this._renderer.render(this._scene, this._camera);
    }

    constructor(params: TWorldParams) {
        this._params = params;
        this._container = params.container;
        this._canvas = params.webGLParameters.canvas;
        this._renderer = new WebGLRenderer(params.webGLParameters);
        this._renderer.setSize(this.containerSize.width, this.containerSize.height);

        this._scene = new SceneWithDispatcher();
        this._camera = new PerspectiveCamera();
        World._worlds.push(this);
        World.initResizer();

        this._mainLoop = new MainLoop(params.mainLoopParams);
        this._mainLoop.idleFps = -1;
        this._mainLoop.addCallback(this.render.bind(this));
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    render(loopInfo?: TLoopInfo) {
        this._renderer.render(this._scene, this._camera);
    }

    dispose() {
        this._renderer.dispose();
        // if the context is already lost do not try to force it
        if (!this._renderer.getContext().isContextLost()) {
            this._renderer.forceContextLoss();
        }

        this._mainLoop.dispose();
        World._worlds = World._worlds.filter((world: IWorld) => world !== this);
    }

    get containerSize(): ContainerSize {
        if (this._container instanceof Window) {
            return {
                width: this._container.innerWidth,
                height: this._container.innerHeight,
                aspect: this._container.innerWidth / this._container.innerHeight,
            };
        }
        if (this._container instanceof HTMLElement) {
            return {
                width: this._container.clientWidth,
                height: this._container.clientHeight,
                aspect: this._container.clientWidth / this._container.clientHeight,
            };
        }
        throw new Error('container is not HTMLElement or Window');
    }

    resize() {
        if (this._camera instanceof PerspectiveCamera) {
            const cam: PerspectiveCamera = this._camera;
            cam.aspect = this.containerSize.aspect;
            cam.updateProjectionMatrix();
        }

        this._renderer.setSize(this.containerSize.width, this.containerSize.height);
        this._renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
        this._renderer.render(this._scene, this._camera);
    }

    launchFullScreen = () => {
        if (!this.canvas) {
            return;
        }
        if (!document.fullscreenElement) {
            (this.canvas as HTMLCanvasElement)?.requestFullscreen();
            return;
        }
        document.exitFullscreen();
    };

    static initResizer() {
        if (World.resizerInitialised) return;
        World.resizerInitialised = true;
        window.addEventListener('resize', () => {
            World._worlds.forEach((world: IWorld) => world.resize());
        });
    }
}

export { World };
